// src/scenes/sitemanager/SiteReportCard.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../component-files/Header";
import { useAuth } from "../../context/AuthContext";

// ---- NEW: Excel export libs ----
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/* ---------- helpers ---------- */
const normalizeYM = (ym) => String(ym ?? "").replace(/\s+/g, ""); // "2025- 08" -> "2025-08"
const toNum = (v) => Number(v ?? 0);
const fmt2 = (v) => (v ?? 0).toFixed(2);

export default function SiteReportCard() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations, isSchoolUser } = useAuth(); // available if you later want to filter to manager sites

    const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
    const [monthFilter, setMonthFilter] = useState("");

    // raw
    const [adpData, setAdpData] = useState([]);
    const [incData, setIncData] = useState([]);
    const [expData, setExpData] = useState([]);
    const [mplhData, setMplhData] = useState([]);

    // pickers
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    useEffect(() => {
        (async () => {
            const [adpRes, incRes, expRes, mplhRes] = await Promise.all([
                axios.get("https://figazuresql.swancreate.com/api/swa/vw_ADP_Monthly"),
                axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_IncomePerMEQBySiteMonth"),
                axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_ExpensePerMEQBySiteMonth"),
                axios.get("https://figazuresql.swancreate.com/api/swa/vw_KPI_MealsPerLaborHourBySiteMonth"),
            ]);

            // --- normalize everything to SchoolID + clean YearMonth ---
            const adp = (adpRes.data || []).map((r) => ({
                SchoolID: r.SchoolID ?? r.SchoolId,
                SchoolName: r.SchoolName,
                YearMonth: normalizeYM(r.YearMonth),
                ADP_MonthlyPct: toNum(r.ADP_MonthlyPct),
            }));

            const inc = (incRes.data || []).map((r) => ({
                SchoolID: r.SchoolID ?? r.SchoolId,
                SchoolName: r.SchoolName,
                YearMonth: normalizeYM(r.YearMonth),
                TotalIncomes: toNum(r.TotalIncomes),
                TotalMEQ: toNum(r.TotalMEQ),
                IncomePerMEQ: toNum(r.IncomePerMEQ),
            }));

            const exp = (expRes.data || []).map((r) => ({
                SchoolID: r.SchoolID ?? r.SchoolId,
                SchoolName: r.SchoolName,
                YearMonth: normalizeYM(r.YearMonth),
                TotalExpenses: toNum(r.TotalExpenses),
                TotalMEQ: toNum(r.TotalMEQ),
                ExpensePerMEQ: toNum(r.ExpensePerMEQ),
            }));

            const mplh = (mplhRes.data || []).map((r) => ({
                SchoolID: r.SchoolID ?? r.SchoolId,
                SchoolName: r.SchoolName,
                YearMonth: normalizeYM(r.YearMonth),
                TotalMEQ: toNum(r.TotalMEQ),
                TotalLaborHours: toNum(r.TotalLaborHours),
                MPLH: toNum(r.MPLH),
            }));

            // --- build Year/Month pickers from union of sources
            const ySet = new Set();
            const mSet = new Set();
            [...adp, ...inc, ...exp, ...mplh].forEach((r) => {
                if (!r.YearMonth) return;
                const [y] = r.YearMonth.split("-");
                ySet.add(y);
                mSet.add(r.YearMonth);
            });
            setYears(Array.from(ySet).sort());
            setMonths(Array.from(mSet).sort());

            setAdpData(adp);
            setIncData(inc);
            setExpData(exp);
            setMplhData(mplh);
        })();
    }, [allocations, isSchoolUser]);

    // filter to current selection
    const filterByPeriod = (arr) =>
        arr.filter(
            (r) =>
                (yearFilter ? r.YearMonth?.startsWith(yearFilter) : true) &&
                (monthFilter ? r.YearMonth === monthFilter : true)
        );

    const adpFy = useMemo(() => filterByPeriod(adpData), [adpData, yearFilter, monthFilter]);
    const incFy = useMemo(() => filterByPeriod(incData), [incData, yearFilter, monthFilter]);
    const expFy = useMemo(() => filterByPeriod(expData), [expData, yearFilter, monthFilter]);
    const mplhFy = useMemo(() => filterByPeriod(mplhData), [mplhData, yearFilter, monthFilter]);

    // sites list (using unified SchoolID)
    const sites = useMemo(() => {
        const map = new Map();
        [...adpFy, ...incFy, ...expFy, ...mplhFy].forEach((r) => {
            if (r.SchoolID) map.set(r.SchoolID, r.SchoolName);
        });
        return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [adpFy, incFy, expFy, mplhFy]);

    // ranking + values per metric (aggregated correctly)
    const metricRanks = useMemo(() => {
        const out = {};
        const N = sites.length;

        const bySite = (arr) => {
            const m = new Map(); // id -> {name, ...totals}
            arr.forEach((r) => {
                const g =
                    m.get(r.SchoolID) || {
                        name: r.SchoolName,
                        meq: 0,
                        inc: 0,
                        exp: 0,
                        hrs: 0,
                        adpSum: 0,
                        adpN: 0,
                    };
                if ("ADP_MonthlyPct" in r) {
                    g.adpSum += toNum(r.ADP_MonthlyPct);
                    g.adpN += 1;
                }
                if ("TotalIncomes" in r) {
                    g.inc += toNum(r.TotalIncomes);
                    g.meq += toNum(r.TotalMEQ);
                }
                if ("TotalExpenses" in r) {
                    g.exp += toNum(r.TotalExpenses);
                    g.meq += toNum(r.TotalMEQ);
                }
                if ("TotalLaborHours" in r) {
                    g.hrs += toNum(r.TotalLaborHours);
                    g.meq += toNum(r.TotalMEQ);
                }
                m.set(r.SchoolID, g);
            });
            return m;
        };

        const adpBy = bySite(adpFy);
        const incBy = bySite(incFy);
        const expBy = bySite(expFy);
        const mplhBy = bySite(mplhFy);

        // build list with aggregated values
        const list = sites.map((s) => {
            const a = adpBy.get(s.id) || {};
            const i = incBy.get(s.id) || {};
            const e = expBy.get(s.id) || {};
            const m = mplhBy.get(s.id) || {};

            const adpVal = a.adpN > 0 ? a.adpSum / a.adpN : 0;
            const incVal = i.meq > 0 ? i.inc / i.meq : 0; // ΣIncome ÷ ΣMEQ
            const expVal = e.meq > 0 ? e.exp / e.meq : 0; // ΣExpense ÷ ΣMEQ
            const mplhVal = m.hrs > 0 ? m.meq / m.hrs : 0; // ΣMEQ ÷ ΣHours

            return { id: s.id, name: s.name, adpVal, incVal, expVal, mplhVal };
        });

        // rank per metric
        const rankOn = (key, rankField) => {
            const arr = [...list].sort((a, b) => b[key] - a[key]);
            arr.forEach((d, i) => {
                out[d.id] = out[d.id] || {};
                out[d.id][rankField] = i + 1;
            });
        };

        rankOn("adpVal", "adpRank");
        rankOn("incVal", "incRank");
        rankOn("expVal", "expRank");
        rankOn("mplhVal", "mplhRank");

        // stash values + combined score
        list.forEach((d) => {
            const m = out[d.id] || {};
            m.adpVal = d.adpVal;
            m.incVal = d.incVal;
            m.expVal = d.expVal;
            m.mplhVal = d.mplhVal;
            m.combined = Math.round(
                ((N - m.adpRank + 1) / N +
                    (N - m.incRank + 1) / N +
                    (N - m.expRank + 1) / N +
                    (N - m.mplhRank + 1) / N) /
                4 *
                100
            );
            out[d.id] = m;
        });

        return out;
    }, [sites, adpFy, incFy, expFy, mplhFy]);

    // grid rows
    const rows = useMemo(
        () =>
            sites.map((s) => {
                const m = metricRanks[s.id] || {};
                return {
                    id: s.id,
                    site: s.name,
                    adp: Math.round(m.adpVal || 0),
                    adpRank: m.adpRank,
                    incomePerMEQ: fmt2(m.incVal || 0),
                    incomeRank: m.incRank,
                    expensePerMEQ: fmt2(m.expVal || 0),
                    expenseRank: m.expRank,
                    mplh: fmt2(m.mplhVal || 0),
                    mplhRank: m.mplhRank,
                    score: m.combined ?? 0,
                };
            }),
        [sites, metricRanks]
    );

    // leaderboards (already sorted)
    const adpLb = [...rows].sort((a, b) => (a.adpRank ?? Infinity) - (b.adpRank ?? Infinity));
    const incLb = [...rows].sort((a, b) => (a.incomeRank ?? Infinity) - (b.incomeRank ?? Infinity));
    const expLb = [...rows].sort((a, b) => (a.expenseRank ?? Infinity) - (b.expenseRank ?? Infinity));
    const mplhLb = [...rows].sort((a, b) => (a.mplhRank ?? Infinity) - (b.mplhRank ?? Infinity));
    const scoreLb = [...rows].sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));

    const glowMap = {
        ADP: "rgba(168,230,207,0.6)",
        "Income/MEQ": "rgba(255,211,182,0.6)",
        "Expense/MEQ": "rgba(195,174,214,0.6)",
        MPLH: "rgba(253,253,150,0.6)",
        Score: "rgba(180,200,255,0.6)",
    };

    /* ---------- NEW: Excel (XLSX) with styling ---------- */
    const downloadLeaderboardsExcel = async () => {
        const periodLabel = `Period: ${yearFilter}${monthFilter ? "-" + monthFilter.split("-")[1] : " (All Months)"}`;

        // helper to build a leaderboard sheet
        const addLeaderboardSheet = (wb, title, headers, rowsArr, headerColor, valueNumFmt) => {
            const ws = wb.addWorksheet(title, {
                views: [{ state: "frozen", ySplit: 3 }],
                properties: { defaultRowHeight: 18 },
            });

            const colCount = headers.length;

            // Title row (A1: merged)
            ws.mergeCells(1, 1, 1, colCount);
            ws.getCell(1, 1).value = title;
            ws.getCell(1, 1).font = { bold: true, size: 16 };
            ws.getCell(1, 1).alignment = { vertical: "middle", horizontal: "center" };

            // Period row (A2: merged)
            ws.mergeCells(2, 1, 2, colCount);
            ws.getCell(2, 1).value = periodLabel;
            ws.getCell(2, 1).font = { italic: true, color: { argb: "FF666666" } };
            ws.getCell(2, 1).alignment = { horizontal: "center" };

            // Header row (row 3)
            headers.forEach((h, i) => {
                const cell = ws.getCell(3, i + 1);
                cell.value = h;
                cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: headerColor } };
                cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                cell.border = {
                    top: { style: "thin", color: { argb: "FFCCCCCC" } },
                    left: { style: "thin", color: { argb: "FFCCCCCC" } },
                    bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
                    right: { style: "thin", color: { argb: "FFCCCCCC" } },
                };
            });

            // Column widths
            ws.getColumn(1).width = 7; // Rank
            ws.getColumn(2).width = 34; // Site
            for (let c = 3; c <= colCount; c++) ws.getColumn(c).width = 14;

            // Data rows start at row 4
            rowsArr.forEach((r, idx) => {
                const row = ws.addRow(r);
                // Zebra striping
                if (idx % 2 === 1) {
                    row.eachCell((cell) => {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFF7F7F7" },
                        };
                    });
                }
                // Borders + formats
                row.eachCell((cell, colNumber) => {
                    cell.border = {
                        top: { style: "thin", color: { argb: "FFEDEDED" } },
                        left: { style: "thin", color: { argb: "FFEDEDED" } },
                        bottom: { style: "thin", color: { argb: "FFEDEDED" } },
                        right: { style: "thin", color: { argb: "FFEDEDED" } },
                    };
                    if (colNumber === 1) cell.alignment = { horizontal: "center" }; // rank center
                    if (colNumber === 3 && valueNumFmt) cell.numFmt = valueNumFmt;
                });
            });

            // Add a table for nicer Excel visuals
            ws.addTable({
                name: title.replace(/\W+/g, "") + "Table",
                ref: "A3",
                headerRow: true,
                totalsRow: false,
                style: {
                    theme: "TableStyleMedium9",
                    showRowStripes: true,
                },
                columns: headers.map((h) => ({ name: h })),
                rows: rowsArr,
            });
        };

        const wb = new ExcelJS.Workbook();
        wb.creator = "Site Report Card";
        wb.created = new Date();

        // Build sheets
        addLeaderboardSheet(
            wb,
            "ADP",
            ["Rank", "Site", "ADP (%)"],
            adpLb.map((d, i) => [i + 1, d.site, Number(d.adp || 0)]),
            "FF59A985", // green-ish
            "0.00"
        );

        addLeaderboardSheet(
            wb,
            "Income per MEQ",
            ["Rank", "Site", "Income/MEQ"],
            incLb.map((d, i) => [i + 1, d.site, Number(d.incomePerMEQ || 0)]),
            "FFFFA15A", // orange-ish
            "0.00"
        );

        addLeaderboardSheet(
            wb,
            "Expense per MEQ",
            ["Rank", "Site", "Expense/MEQ"],
            expLb.map((d, i) => [i + 1, d.site, Number(d.expensePerMEQ || 0)]),
            "FF7E57C2", // purple
            "0.00"
        );

        addLeaderboardSheet(
            wb,
            "MPLH",
            ["Rank", "Site", "MPLH"],
            mplhLb.map((d, i) => [i + 1, d.site, Number(d.mplh || 0)]),
            "FFF4D03F", // yellow
            "0.00"
        );

        addLeaderboardSheet(
            wb,
            "Score",
            ["Rank", "Site", "Score (%)"],
            scoreLb.map((d, i) => [i + 1, d.site, Number(d.score || 0)]),
            "FF64B5F6", // blue
            "0.00"
        );

        // Summary sheet
        const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + Number(v || 0), 0) / arr.length : 0);
        const adpAvg = avg(adpLb.map((d) => Number(d.adp)));
        const incAvg = avg(incLb.map((d) => Number(d.incomePerMEQ)));
        const expAvg = avg(expLb.map((d) => Number(d.expensePerMEQ)));
        const mplhAvg = avg(mplhLb.map((d) => Number(d.mplh)));
        const scoreAvg = avg(scoreLb.map((d) => Number(d.score)));

        const ws = wb.addWorksheet("Summary", {
            views: [{ state: "frozen", ySplit: 3 }],
        });
        ws.mergeCells("A1:C1");
        ws.getCell("A1").value = "Leaderboards — Totals & Averages";
        ws.getCell("A1").font = { bold: true, size: 16 };
        ws.getCell("A1").alignment = { horizontal: "center" };

        ws.mergeCells("A2:C2");
        ws.getCell("A2").value = periodLabel;
        ws.getCell("A2").font = { italic: true, color: { argb: "FF666666" } };
        ws.getCell("A2").alignment = { horizontal: "center" };

        ws.getColumn(1).width = 24;
        ws.getColumn(2).width = 16;
        ws.getColumn(3).width = 16;

        // Header
        ["Metric", "Average", ""].forEach((h, i) => {
            const cell = ws.getCell(3, i + 1);
            cell.value = h;
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF455A64" } };
            cell.alignment = { horizontal: "center" };
        });

        // Rows
        const summaryRows = [
            ["ADP (%)", adpAvg, ""],
            ["Income/MEQ", incAvg, ""],
            ["Expense/MEQ", expAvg, ""],
            ["MPLH", mplhAvg, ""],
            ["Score (%)", scoreAvg, ""],
        ];

        summaryRows.forEach((r, idx) => {
            const row = ws.addRow(r);
            row.getCell(2).numFmt = "0.00";
            if (idx % 2 === 1) {
                row.eachCell((cell) => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFF7F7F7" },
                    };
                });
            }
        });

        // File
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, "Leaderboards_and_Totals.xlsx");
    };

    /* ---------- CSV: grid rows (kept) ---------- */
    const downloadGridCsv = () => {
        const hdr = [
            "Site",
            "ADP (%)",
            "ADP Rank",
            "Income/MEQ",
            "Inc Rank",
            "Expense/MEQ",
            "Exp Rank",
            "MPLH",
            "MPLH Rank",
            "Score",
        ];
        const csv = [
            hdr.join(","),
            ...rows.map((r) =>
                [
                    r.site,
                    r.adp,
                    r.adpRank ?? "",
                    r.incomePerMEQ,
                    r.incomeRank ?? "",
                    r.expensePerMEQ,
                    r.expenseRank ?? "",
                    r.mplh,
                    r.mplhRank ?? "",
                    r.score,
                ].join(",")
            ),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "SiteReportCard.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const columns = [
        { field: "site", headerName: "Site", flex: 1, minWidth: 180 },
        { field: "adpRank", headerName: "ADP Rank", type: "number", width: 100 },
        { field: "adp", headerName: "Avg ADP (%)", type: "number", width: 120 },
        { field: "incomeRank", headerName: "Inc Rank", type: "number", width: 100 },
        { field: "incomePerMEQ", headerName: "Income/MEQ", type: "number", width: 130 },
        { field: "expenseRank", headerName: "Exp Rank", type: "number", width: 100 },
        { field: "expensePerMEQ", headerName: "Expense/MEQ", type: "number", width: 130 },
        { field: "mplhRank", headerName: "MPLH Rank", type: "number", width: 100 },
        { field: "mplh", headerName: "MPLH", type: "number", width: 100 },
        { field: "score", headerName: "Score (%)", type: "number", width: 120 },
    ];

    return (
        <Box p={3}>
            <Header title="Site Report Card" subtitle="Combined ranking across four metrics" />

            {/* Leaderboards header + Excel button */}
            <Box display="flex" alignItems="center" mb={2} gap={1}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                    Leaderboards
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    onClick={downloadLeaderboardsExcel}
                    sx={{
                        backgroundColor: theme.palette.success.main,
                        color: theme.palette.getContrastText(theme.palette.success.main),
                        "&:hover": { backgroundColor: theme.palette.success.dark },
                    }}
                >
                    Download All (Excel)
                </Button>
            </Box>

            {/* Leaderboards: equal widths via responsive CSS grid */}
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    mb: 4,
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(5, 1fr)", // five equal columns on large screens
                    },
                }}
            >
                {/* ADP card */}
                <Card sx={{ p: 2, boxShadow: `0 0 20px ${glowMap.ADP}` }}>
                    <Typography variant="h6" gutterBottom>
                        🏆 Top ADP (%)
                    </Typography>
                    <Box
                        component="ol"
                        sx={{
                            pl: 2,
                            m: 0,
                            maxHeight: 300,
                            overflowY: "auto",
                            "& li": { display: "flex", justifyContent: "space-between", py: 0.5 },
                            "& li:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
                        }}
                    >
                        {adpLb.map((d, i) => (
                            <Typography key={d.id} component="li">
                                <span>{`${i + 1}. ${d.site}`}</span>
                                <span>{d.adp}%</span>
                            </Typography>
                        ))}
                    </Box>
                </Card>

                {/* Other leaderboards */}
                {[
                    { title: "Income/MEQ", data: incLb, glow: glowMap["Income/MEQ"] },
                    { title: "Expense/MEQ", data: expLb, glow: glowMap["Expense/MEQ"] },
                    { title: "MPLH", data: mplhLb, glow: glowMap.MPLH },
                    { title: "Score", data: scoreLb, glow: glowMap.Score },
                ].map(({ title, data, glow }) => (
                    <Card key={title} sx={{ p: 2, boxShadow: `0 0 20px ${glow}` }}>
                        <Typography variant="h6" gutterBottom>
                            🏆 Top {title}
                        </Typography>
                        <Box
                            component="ol"
                            sx={{
                                pl: 2,
                                m: 0,
                                maxHeight: 300,
                                overflowY: "auto",
                                "& li": { display: "flex", justifyContent: "space-between", py: 0.5 },
                                "& li:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
                            }}
                        >
                            {data.map((d, i) => (
                                <Typography key={d.id} component="li">
                                    <span>{`${i + 1}. ${d.site}`}</span>
                                    <span>
                                        {title === "Score"
                                            ? `${d.score}%`
                                            : title === "Income/MEQ"
                                                ? d.incomePerMEQ
                                                : title === "Expense/MEQ"
                                                    ? d.expensePerMEQ
                                                    : title === "MPLH"
                                                        ? d.mplh
                                                        : ""}
                                    </span>
                                </Typography>
                            ))}
                        </Box>
                    </Card>
                ))}
            </Box>

            {/* Filters + Grid CSV */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                        {years.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        {months
                            .filter((m) => (yearFilter ? m.startsWith(yearFilter) : true))
                            .map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <Button variant="contained" sx={{ ml: "auto" }} onClick={downloadGridCsv}>
                    Download Grid (CSV)
                </Button>
            </Box>

            {/* Data grid */}
            <Card>
                <CardContent>
                    <Box sx={{ height: 600, width: "100%" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pageSize={20}
                            rowsPerPageOptions={[20, 50, 100]}
                            pagination
                            disableSelectionOnClick
                            sortModel={[{ field: "score", sort: "desc" }]}
                            sx={{
                                "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
